import { useCallback, useEffect, useRef } from 'react';

import { useFormikContext } from 'formik';

import { useAppSelector } from '../../../../../custom-hooks/useAppSelector';
import { validateBankAccount } from '../../../../../services/bankaccountlookup.service';

interface FieldStates {
  bankName: boolean;
  bankAddress: boolean;
  swift: boolean;
}

interface UseBankAddressLookupReturn {
  triggerValidation: () => void;
}

// Hook to lookup bank address based on IBAN or Sort Code
export const useBankAddressLookup = (
  setDisabledBankAccountFields: React.Dispatch<React.SetStateAction<FieldStates>>,
  setLoadingFieldsDuringAccountValidation: React.Dispatch<React.SetStateAction<FieldStates>>,
  debounceTimerRef: React.MutableRefObject<NodeJS.Timeout | null>
): UseBankAddressLookupReturn => {
  const { values, setFieldValue } = useFormikContext<any>();
  const orgId = useAppSelector((state) => state.currentApp?.OrgId ?? '');
  const previousIbanRef = useRef<string>('');
  const previousSortCodeRef = useRef<string>('');

  const getPopulatedFieldValue = useCallback((displayName: string) => {
    const populatedFields = values.PopulatedAccountFields || [];
    const field = populatedFields.find((field: any) => field.DisplayName === displayName);
    return field?.Value || '';
  }, [values.PopulatedAccountFields]);

  const lookupBankAddress = useCallback(async (iban: string, sortCode: string, accountNumber: string) => {
    try {
      // Get country code from form values
      const countryCode = values.BankCountryCode || '';
      
      if (!orgId || !countryCode) {
        console.log('Missing required parameters for bank account validation');
        return;
      }

      // Set loading state
      setLoadingFieldsDuringAccountValidation({
        bankName: true,
        bankAddress: true,
        swift: true,
      });

      console.log(`Looking up bank address for country: ${countryCode} IBAN: ${iban} (and sort code: ${sortCode})`);
      const response = await validateBankAccount(orgId, {
        countryCode,
        iban: iban || '',
        sortCode: sortCode || '',
        accountNumber: accountNumber || '',
      });
      console.log('Response from bank account validation: ' + JSON.stringify(response));

      const fieldsSuccessfullyPopulated = {
        bankName: false,
        bankAddress: false,
        swift: false,
      };

      // Only proceed if validation was successful
      if (response.valid) {
        // Only update BankAddress if the API returns a non-empty string
        if (response.bankAddress && typeof response.bankAddress === 'string' && response.bankAddress.trim()) {
          setFieldValue('BankAddress', response.bankAddress);
          fieldsSuccessfullyPopulated.bankAddress = true; // Keep disabled
        } else {
          fieldsSuccessfullyPopulated.bankAddress = false; // Enable for manual entry
        }

        // Only update BankName if the API returns a non-empty string
        if (response.bankName && typeof response.bankName === 'string' && response.bankName.trim()) {
          setFieldValue('BankName', response.bankName);
          fieldsSuccessfullyPopulated.bankName = true; // Keep disabled
        } else {
          fieldsSuccessfullyPopulated.bankName = false; // Enable for manual entry
        }

        if (response.bic && typeof response.bic === 'string' && response.bic.trim()) {
          // Find the Swift field in PopulatedAccountFields and update it
          const populatedFields = values.PopulatedAccountFields || [];
          const swiftFieldIndex = populatedFields.findIndex(
            (field: any) => field.DisplayName === 'Swift'
          );
          if (swiftFieldIndex !== -1) {
            const fieldPath = `PopulatedAccountFields.${swiftFieldIndex}.Value`;
            setFieldValue(fieldPath, response.bic);
            fieldsSuccessfullyPopulated.swift = true; // Keep disabled
          } else {
            console.warn('Swift field not found in PopulatedAccountFields array - this should not happen!');
            fieldsSuccessfullyPopulated.swift = false; // Enable for manual entry
          }
        } else {
          console.log('BIC value not valid or empty, enabling field for manual entry');
          fieldsSuccessfullyPopulated.swift = false; // Enable for manual entry
        }
      } else {
        // Validation failed, enable all fields for manual entry
        fieldsSuccessfullyPopulated.bankName = false;
        fieldsSuccessfullyPopulated.bankAddress = false;
        fieldsSuccessfullyPopulated.swift = false;
        
        if (response.errorMessage) {
          console.warn('Bank account validation failed:', response.errorMessage);
        }
      }

      setDisabledBankAccountFields(fieldsSuccessfullyPopulated);
      setLoadingFieldsDuringAccountValidation({
        bankName: false,
        bankAddress: false,
        swift: false,
      });
    } catch (error) {
      console.warn('Couldn\'t find bank address by account/IBAN:', error);
      setDisabledBankAccountFields({
        bankName: false,
        bankAddress: false,
        swift: false,
      });
      setLoadingFieldsDuringAccountValidation({
        bankName: false,
        bankAddress: false,
        swift: false,
      });
    }
  }, [values.BankCountryCode, values.PopulatedAccountFields, orgId, setFieldValue, setDisabledBankAccountFields, setLoadingFieldsDuringAccountValidation]);

  // Function to trigger validation immediately (exposed for external use)
  const triggerValidation = useCallback(() => {
    const currentIban = getPopulatedFieldValue('IBAN');
    const currentSortCode = getPopulatedFieldValue('Sort Code') || getPopulatedFieldValue('Routing Number');
    const currentAccountNumber = getPopulatedFieldValue('Account Number');

    if (currentIban || currentSortCode) {
      lookupBankAddress(currentIban, currentSortCode, currentAccountNumber);
    }
  }, [getPopulatedFieldValue, lookupBankAddress]);

  useEffect(() => {
    // Extract IBAN and Sort Code from PopulatedAccountFields
    const currentIban = getPopulatedFieldValue('IBAN');
    const currentSortCode = getPopulatedFieldValue('Sort Code') || getPopulatedFieldValue('Routing Number');
    const currentAccountNumber = getPopulatedFieldValue('Account Number');

    // Check if IBAN has changed and has a value
    const ibanChanged = currentIban && currentIban !== previousIbanRef.current;
    const sortCodeChanged = currentSortCode && currentSortCode !== previousSortCodeRef.current;

    if (ibanChanged || sortCodeChanged) {
      previousIbanRef.current = currentIban;
      previousSortCodeRef.current = currentSortCode;

      // Clear any existing timeout
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        lookupBankAddress(currentIban, currentSortCode, currentAccountNumber);
      }, 1000);
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [getPopulatedFieldValue, lookupBankAddress, debounceTimerRef]);

  return {
    triggerValidation,
  };
};

